/**
 * Runtime manifest branding (ADR-0004).
 *
 * One static deployment serves many `mail.<domain>` hosts, but an install
 * banner that says "Mail" on every domain is not good enough: the manifest
 * the browser shows must carry the serving domain's name. The emitted
 * manifest is fetched, its names are rewritten from the hostname, and the
 * `<link rel="manifest">` is re-pointed at a blob copy. Failure falls back
 * to the static manifest — branding degrades, nothing breaks.
 */
import { brandFromHostname } from "./branding.ts";

/**
 * The subset of the web app manifest this module rewrites.
 */
interface WebManifest {
  /**
  Full install name.
   */
  name?: string;
  /**
  Short name for icons and banners.
   */
  short_name?: string;
}

/**
 * Rewrites the document's manifest link with hostname-derived branding.
 *
 * @param doc - The document whose manifest link is rewritten.
 * @param fetcher - Fetch used to load the emitted manifest.
 * @param makeObjectUrl - Object-URL factory (injectable for tests).
 * @returns Resolves when the link has been rewritten, or immediately when
 * there is no manifest link / the fetch fails (static manifest stays).
 */
export async function brandManifest(
  doc: Document = document,
  fetcher: typeof fetch = fetch,
  makeObjectUrl: (blob: Blob) => string = (blob) => URL.createObjectURL(blob),
): Promise<void> {
  const link = doc.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (link === null) {
    return;
  }
  const href = link.getAttribute("href");
  if (href === null) {
    return;
  }
  let manifest: WebManifest;
  try {
    const response = await fetcher(href);
    if (!response.ok) {
      return;
    }
    manifest = (await response.json()) as WebManifest;
  } catch {
    return;
  }
  const brand = brandFromHostname(doc.location.hostname);
  const branded = {
    ...manifest,
    name: brand.title,
    short_name: brand.title,
  };
  link.href = makeObjectUrl(
    new Blob([JSON.stringify(branded)], { type: "application/manifest+json" }),
  );
}
